import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  UserResponse,
  MemberResponse,
  LoginRequest,
  RegisterRequest,
  FamilyResponse
} from '@/types/api'
import {
  loginUseCase,
  registerUseCase,
  logoutUseCase,
  loadSessionUseCase,
  loadPersistedFamily,
  loadPersistedMember,
  refreshUserUseCase,
  isTokenValid,
  displayNameOf,
  firstNameOf,
  type AuthState as CoreAuthState
} from '@pepi/core'
import { getApiClientConfig } from '@pepi/api-client'
import { useFamilyStore } from '@/stores/family'

/**
 * Sincroniza las credenciales con el tokenStorage del API client
 */
const syncToApiClientStorage = (
  tokenValue: string | null,
  refreshTokenValue: string | null,
  userValue: UserResponse | null
) => {
  const config = getApiClientConfig()
  if (!config?.tokenStorage) return

  if (tokenValue) {
    config.tokenStorage.setToken(tokenValue)
  } else {
    config.tokenStorage.clearAll()
    return
  }

  if (refreshTokenValue) {
    config.tokenStorage.setRefreshToken(refreshTokenValue)
  }

  if (userValue) {
    config.tokenStorage.setUser(userValue)
  }
}

/**
 * Store de autenticación
 *
 * Solo orquesta llamadas a casos de uso de @pepi/core.
 * No contiene lógica de negocio - solo mantiene estado reactivo.
 */
export const useAuthStore = defineStore('auth', () => {
  // ============== STATE ==============
  const user = ref<UserResponse | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  /** Ficha de miembro del propio usuario: es donde viaja su NOMBRE. */
  const member = ref<MemberResponse | null>(null)
  const loading = ref(false)

  // ============== GETTERS ==============
  const isAuthenticated = computed(() => {
    return !!(token.value && user.value && isTokenValid(token.value))
  })

  /**
   * Cómo se llama quien ha entrado. Lo resuelve `@pepi/core` en un solo sitio:
   * `user.name` NO existe en el DTO, así que cada pantalla que lo daba por
   * hecho se lo inventaba a su manera.
   */
  const displayName = computed(() => displayNameOf(member.value, user.value, 'Tu cuenta'))
  const firstName = computed(() => firstNameOf(member.value, user.value, ''))

  const authState = computed((): CoreAuthState => ({
    isAuthenticated: isAuthenticated.value,
    user: user.value,
    token: token.value,
    refreshToken: refreshToken.value,
    loading: loading.value,
  }))

  // ============== HELPERS ==============

  /**
   * Propaga la familia activa del usuario al store de familias.
   *
   * Centraliza la inicialización para que las vistas sin `:id` en la URL
   * dispongan del familyId real del usuario (evita fallbacks hardcodeados
   * tipo `?? 1`). Si no se pasa familia, el store de familias cae a la
   * primera del usuario.
   */
  const syncActiveFamily = async (family: FamilyResponse | null = null): Promise<void> => {
    const familyStore = useFamilyStore()
    await familyStore.ensureActiveFamily(family)
  }

  // ============== ACTIONS ==============

  /**
   * Iniciar sesión usando caso de uso del dominio
   */
  const login = async (credentials: LoginRequest): Promise<void> => {
    loading.value = true
    try {
      const authCredentials = await loginUseCase(credentials)

      // Actualizar estado local
      token.value = authCredentials.token
      refreshToken.value = authCredentials.refreshToken
      user.value = authCredentials.user
      member.value = authCredentials.member

      // Sincronizar con API client storage
      syncToApiClientStorage(
        authCredentials.token,
        authCredentials.refreshToken,
        authCredentials.user
      )

      // Inicializar la familia activa del usuario (fuente: backend en login/register)
      await syncActiveFamily(authCredentials.family)
    } catch (error) {
      clearAuth()
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Registrar usuario usando caso de uso del dominio
   */
  const register = async (userData: RegisterRequest): Promise<void> => {
    loading.value = true
    try {
      const authCredentials = await registerUseCase(userData)

      // Actualizar estado local
      token.value = authCredentials.token
      refreshToken.value = authCredentials.refreshToken
      user.value = authCredentials.user
      member.value = authCredentials.member

      // Sincronizar con API client storage
      syncToApiClientStorage(
        authCredentials.token,
        authCredentials.refreshToken,
        authCredentials.user
      )

      // Inicializar la familia activa del usuario (fuente: backend en login/register)
      await syncActiveFamily(authCredentials.family)
    } catch (error) {
      clearAuth()
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Cerrar sesión usando caso de uso del dominio
   */
  const logout = async (): Promise<void> => {
    loading.value = true
    try {
      await logoutUseCase()
    } finally {
      clearAuth()
      loading.value = false
    }
  }

  /**
   * Limpiar estado de autenticación local
   */
  const clearAuth = (): void => {
    user.value = null
    member.value = null
    token.value = null
    refreshToken.value = null

    // Limpiar también el storage del API client
    syncToApiClientStorage(null, null, null)

    // Resetear el store de familias para no arrastrar datos de la sesión anterior
    useFamilyStore().reset()
  }

  /**
   * Cargar sesión desde almacenamiento usando caso de uso del dominio
   */
  const loadFromStorage = async (): Promise<void> => {
    // Primero intentar cargar desde el API client storage (localStorage)
    const config = getApiClientConfig()
    if (config?.tokenStorage) {
      const storedToken = config.tokenStorage.getToken()
      const storedUser = config.tokenStorage.getUser()
      const storedRefreshToken = config.tokenStorage.getRefreshToken()

      if (storedToken && storedUser && isTokenValid(storedToken)) {
        token.value = storedToken
        refreshToken.value = storedRefreshToken
        user.value = storedUser

        // Rehidratar la familia activa persistida en login/register
        await syncActiveFamily(await loadPersistedFamily())
        // Y la ficha de miembro, que es donde viaja el NOMBRE. Esta vía rápida
        // SALE ANTES de `loadPersistedCredentials`, así que sin esta línea al
        // recargar la web se perdía el nombre aunque la sesión siguiera puesta.
        member.value = await loadPersistedMember()
        return
      }
    }

    // Fallback al use case del core
    const credentials = await loadSessionUseCase()

    if (credentials) {
      token.value = credentials.token
      refreshToken.value = credentials.refreshToken
      user.value = credentials.user
      member.value = credentials.member

      // Sincronizar con API client storage
      syncToApiClientStorage(
        credentials.token,
        credentials.refreshToken,
        credentials.user
      )

      // Rehidratar la familia activa
      await syncActiveFamily(credentials.family)
    } else {
      clearAuth()
    }
  }

  /**
   * Restaura la sesión guardada UNA sola vez por carga de la app; las siguientes llamadas devuelven
   * la misma promesa.
   *
   * La espera el guard del router ANTES de mirar `isAuthenticated`. `app.use(router)` dispara la
   * navegación inicial al instalarse, mucho antes de que `App.vue` se monte (que además espera a la
   * fuente, hasta 1 s): si la restauración viviera solo en el `onMounted` de `App`, toda carga en
   * frío de una ruta protegida —`/familia`, `/agenda`, `/tareas`…— vería el store vacío y acabaría
   * en `/login` con la sesión intacta en el storage.
   *
   * Nunca rechaza: un fallo al restaurar deja la sesión como esté y el guard decide con eso, en vez
   * de abortar la navegación y dejar la pantalla en blanco.
   */
  let sessionRestore: Promise<void> | null = null
  const ensureSessionRestored = (): Promise<void> => {
    sessionRestore ??= loadFromStorage().catch((error) => {
      console.error('Error restoring session:', error)
    })
    return sessionRestore
  }

  /**
   * Actualizar información del usuario usando caso de uso del dominio
   */
  const updateUser = async (): Promise<void> => {
    if (!isAuthenticated.value) return

    try {
      const currentUser = await refreshUserUseCase()
      user.value = currentUser
    } catch (error) {
      console.error('Error updating user:', error)
      // Si falla la actualización, mantener usuario actual
    }
  }

  return {
    // State
    user,
    member,
    token,
    loading,

    // Nombre a mostrar (nunca `user.name`: ese campo no existe)
    displayName,
    firstName,

    // Getters
    isAuthenticated,
    authState,

    // Actions
    login,
    register,
    logout,
    clearAuth,
    loadFromStorage,
    ensureSessionRestored,
    updateUser,
  }
})